import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { ReportHeader } from '@/components/reports/ReportUI';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Scale, AlertCircle, CheckCircle, FileText, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { exportBalanceSheetToPDF } from '@/lib/pdfExport';
import { ExportButtons } from '@/components/ExportButtons';
import { ReconciledBadge } from '@/components/accounting/ReconciledBadge';
import { localToday } from '@/lib/dateRange';
import { useLedgerDrilldown } from '@/components/accounting/LedgerDrilldown';
import { ReportNotifyButton } from '@/components/accounting/ReportNotifyButton';
import { BranchCoverageNotice, forbiddenDetail } from '@/components/accounting/BranchCoverageNotice';
import { useBranchStore } from '@/stores';

function fmt(val) {
  return `${cur()} ${(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BalanceSection({ title, color, items, total, onItemClick, search }) {
  const colorMap = {
    blue: { border: 'border-info/30', text: 'text-info', header: 'bg-info/10', dot: 'bg-info' },
    red: { border: 'border-error/30', text: 'text-error', header: 'bg-error/10', dot: 'bg-error' },
    violet: { border: 'border-violet-200', text: 'text-violet-800', header: 'bg-violet-50', dot: 'bg-violet-500' },
  };
  const c = colorMap[color] || colorMap.blue;
  const q = (search || '').trim().toLowerCase();
  const matches = (name) => !q || name.toLowerCase().includes(q);
  // Hide entire section if nothing matches the search.
  const anyMatch = Object.values(items).some(entries =>
    Object.keys(entries || {}).some(matches),
  );
  if (q && !anyMatch) return null;
  return (
    <Card className={c.border}>
      <div className={`${c.header} px-6 py-3 flex justify-between items-center`}>
        <span className={`font-semibold ${c.text} text-sm flex items-center gap-2`}>
          <div className={`w-2 h-2 rounded-full ${c.dot}`} /> {title}
        </span>
        <span className={`font-bold ${c.text}`}>{fmt(total)}</span>
      </div>
      <CardContent className="p-0">
        {Object.entries(items).map(([section, entries]) => {
          const visibleEntries = Object.entries(entries || {}).filter(([name]) => matches(name));
          if (visibleEntries.length === 0) return null;
          return (
            <div key={section} className="border-t border-border">
              <div className="px-6 py-2 bg-muted/50">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{section.replace(/_/g, ' ')}</span>
              </div>
              {visibleEntries.map(([name, amount]) => (
                <div
                  key={name}
                  className={`flex justify-between px-6 py-2.5 border-t border-stone-50 text-sm ${onItemClick ? 'hover:bg-primary/5 cursor-pointer group' : 'hover:bg-muted/50'}`}
                  onClick={onItemClick ? () => onItemClick(name) : undefined}
                  data-testid={`bs-item-${name}`}
                  title={onItemClick ? `Click to view all journal entries for "${name}"` : undefined}
                >
                  <span className={`pl-3 ${onItemClick ? 'text-foreground group-hover:text-primary transition-colors' : 'text-muted-foreground'}`}>{name}</span>
                  <span className="font-medium text-foreground font-mono text-xs">{fmt(amount)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function BalanceSheetPage() {
  const { t } = useLanguage();
  const { openLedger } = useLedgerDrilldown();
  const [blocked, setBlocked] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(localToday());
  const [branches, setBranches] = useState([]);
  const posBranch = useBranchStore((s) => s.activeBranchId);
  const setPosBranch = useBranchStore((s) => s.setActiveBranchId);
  const [branchSel, setBranchSel] = useState(posBranch ? [posBranch] : []);
  // iter963 — Follow the shared POS/Stock active branch (branchStore).
  useEffect(() => { setBranchSel(posBranch ? [posBranch] : []); }, [posBranch]);
  const [search, setSearch] = useState('');

  // Drill-down: open the reusable ledger modal (stays on the page). Prefer the
  // resolved COA code from `line_accounts` (exact match) and fall back to the
  // display name. Wide start so the modal shows full history up to the as-of date.
  const drillToLedger = (accountName) => {
    const mapped = data?.line_accounts?.[accountName];
    const term = mapped?.code || mapped?.name || accountName;
    openLedger(term, { name: mapped?.name || accountName, startDate: '2000-01-01', endDate: asOfDate });
  };

  const fetchBranches = async () => {
    try { const res = await api.get('/branches'); setBranches(res.data || []); } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ as_of_date: asOfDate });
      if (branchSel.length) params.append('branch_ids', branchSel.join(','));
      const res = await api.get(`/accounting/balance-sheet?${params}`);
      setData(res.data); setBlocked(null);
    } catch (e) { const d = forbiddenDetail(e); if (d) { setBlocked(d); setData(null); } else toast.error('Failed to load balance sheet'); }
    setLoading(false);
  };

  useEffect(() => { fetchBranches(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [branchSel, asOfDate]);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="balance-sheet-page">
        <ReportHeader icon={Scale} testId="bs-header"
          title={<span className="flex items-center gap-3" data-testid="page-title">Balance Sheet{data && <ReconciledBadge debit={data.assets?.total} credit={data.total_liabilities_equity} difference={(data.assets?.total || 0) - (data.total_liabilities_equity || 0) + (data.unposted_difference || 0)} />}</span>}
          subtitle={data ? `Assets, liabilities and equity as of ${new Date(data.as_of_date).toLocaleDateString()}` : 'Financial position snapshot'}
          actions={<>
            <button onClick={() => exportBalanceSheetToPDF(data, 'Balance Sheet', data?.as_of_date)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50 transition-colors"
              data-testid="export-pdf-btn"><FileText size={14} /> Statement PDF</button>
            <ReportNotifyButton report="balance_sheet" label="Notify" />
          </>} />
        <div className="flex flex-wrap items-center gap-2" data-testid="bs-filter">
          <label className="text-xs text-muted-foreground flex items-center gap-1.5">As of
            <Input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="w-40 h-8 text-xs" data-testid="date-input" />
          </label>
          <BranchMultiSelect value={branchSel} onChange={(v) => { setBranchSel(v); if (v.length <= 1) setPosBranch(v[0] || null); }} branches={branches} testid="branch-filter" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search account…" className="h-8 w-48 text-xs" data-testid="bs-search" />
          <ExportButtons dataType="balance_sheet" branchIds={branchSel} endDate={asOfDate} testid="bs-export" />
        </div>

        <BranchCoverageNotice unavailable={blocked} coverage={data?.branch_coverage} testid="bs-coverage" />
        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-info border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <div id="bs-content" className="space-y-4">
            {/* Balance Check */}
            <Card className={data.is_balanced ? 'border-success/30 bg-success/10' : 'border-warning/30 bg-warning/10'} data-testid="balance-check">
              <CardContent className="p-4 flex items-center gap-3">
                {data.is_balanced ? <CheckCircle size={20} className="text-success" /> : <AlertCircle size={20} className="text-warning" />}
                <div>
                  <p className={`font-semibold text-sm ${data.is_balanced ? 'text-success' : 'text-warning'}`}>
                    {data.is_balanced ? 'Balance Sheet is Balanced' : 'Balance Sheet has a variance'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Assets: {fmt(data.assets.total)} | Liabilities + Equity: {fmt(data.total_liabilities_equity)}
                  </p>
                  {Math.abs(data.unposted_difference || 0) >= 1 && (
                    <p className="text-xs text-warning mt-0.5" data-testid="bs-unposted-difference">
                      Unposted ledger difference: {fmt(data.unposted_difference)}
                      {data.sources?.artificial_adjustment ? ' (shown as a synthetic Suspense line — not a posted balance)' : ''}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Card className="border-info/30">
                <CardContent className="p-4 text-center" data-testid="total-assets">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Assets</p>
                  <p className="text-2xl font-bold text-info mt-1">{fmt(data.assets.total)}</p>
                </CardContent>
              </Card>
              <Card className="border-error/30">
                <CardContent className="p-4 text-center" data-testid="total-liabilities">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Liabilities</p>
                  <p className="text-2xl font-bold text-error mt-1">{fmt(data.liabilities.total)}</p>
                </CardContent>
              </Card>
              <Card className="border-violet-200">
                <CardContent className="p-4 text-center" data-testid="total-equity">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Equity</p>
                  <p className="text-2xl font-bold text-violet-700 mt-1">{fmt(data.equity.total)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Detail Sections */}
            <div className="space-y-4">
              <BalanceSection title="Assets" color="blue"
                items={{ current_assets: data.assets.current_assets, fixed_assets: data.assets.fixed_assets }}
                total={data.assets.total}
                search={search}
                onItemClick={drillToLedger} />
              <BalanceSection title="Liabilities" color="red"
                items={{ current_liabilities: data.liabilities.current_liabilities, long_term_liabilities: data.liabilities.long_term_liabilities }}
                total={data.liabilities.total}
                search={search}
                onItemClick={drillToLedger} />
              <BalanceSection title="Equity" color="violet"
                items={{ equity: data.equity.items }}
                total={data.equity.total}
                search={search}
                onItemClick={drillToLedger} />
            </div>

            {/* Accounting Equation */}
            <Card className="bg-foreground border-stone-700">
              <CardContent className="p-6 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Accounting Equation</p>
                <div className="flex items-center justify-center gap-6 flex-wrap">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Assets</p>
                    <p className="font-bold text-blue-300 text-lg">{fmt(data.assets.total)}</p>
                  </div>
                  <span className="text-muted-foreground text-2xl font-light">=</span>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Liabilities</p>
                    <p className="font-bold text-red-300 text-lg">{fmt(data.liabilities.total)}</p>
                  </div>
                  <span className="text-muted-foreground text-2xl font-light">+</span>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Equity</p>
                    <p className="font-bold text-violet-300 text-lg">{fmt(data.equity.total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
